import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../../../i18n/LanguageProvider';

interface Props {
	musicUrl: string;
	musicName: string;
}

// Cards open muted-to-soft on purpose: 30% is audible without startling a
// recipient who opened the card in a quiet room or a shared office.
const DEFAULT_VOLUME = 0.3;

const MusicPlayer: React.FC<Props> = ({ musicUrl, musicName }) => {
	const { t } = useT();
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [volume, setVolume] = useState(DEFAULT_VOLUME);
	const [showVolume, setShowVolume] = useState(false);

	useEffect(() => {
		if (audioRef.current) {
			audioRef.current.volume = volume;
			audioRef.current.loop = true;
		}
	}, [volume]);

	// Keep the button in sync with the actual element state. Playback can start
	// from the autoplay attempt, the gesture fallback below, or the button — this
	// makes the icon truthful in all three cases (and if the track ever ends).
	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;
		const onPlay = () => setIsPlaying(true);
		const onPause = () => setIsPlaying(false);
		audio.addEventListener('play', onPlay);
		audio.addEventListener('pause', onPause);
		return () => {
			audio.removeEventListener('play', onPlay);
			audio.removeEventListener('pause', onPause);
		};
	}, []);

	// Auto-start at 30% when the card opens. Browsers block audio autoplay until
	// the visitor has interacted with the page, so we try immediately and, if that
	// rejects, arm a one-shot listener that starts the music on the first click,
	// tap, or key — which for the card viewer is the "Click to open" tap anyway.
	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;
		audio.volume = DEFAULT_VOLUME;

		let cleanedUp = false;
		const startOnGesture = () => {
			audio.play().catch(() => {});
			removeGestureListeners();
		};
		const removeGestureListeners = () => {
			if (cleanedUp) return;
			cleanedUp = true;
			['pointerdown', 'keydown', 'touchstart'].forEach((evt) => document.removeEventListener(evt, startOnGesture));
		};

		audio
			.play()
			.catch(() => {
				// Autoplay was blocked — wait for the first interaction instead.
				['pointerdown', 'keydown', 'touchstart'].forEach((evt) =>
					document.addEventListener(evt, startOnGesture, { once: true }),
				);
			});

		return removeGestureListeners;
	}, [musicUrl]);

	const togglePlay = useCallback(() => {
		if (!audioRef.current) return;
		if (isPlaying) {
			audioRef.current.pause();
		} else {
			audioRef.current.play().catch(() => {});
		}
	}, [isPlaying]);

	return (
		<div
			style={{
				position: 'fixed',
				bottom: '24px',
				left: '24px',
				zIndex: 50,
			}}
			className="flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full shadow-lg pl-2 pr-3 py-2 border border-gray-200"
		>
			<audio ref={audioRef} src={musicUrl} preload="auto" />

			<button
				type="button"
				onClick={togglePlay}
				aria-label={isPlaying ? t('music.pause') : t('music.play')}
				aria-pressed={isPlaying}
				title={musicName}
				className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center hover:bg-indigo-700 transition flex-shrink-0 shadow-sm"
			>
				{isPlaying ? (
					<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
						<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
					</svg>
				) : (
					// Nudged right by 1px so the triangle looks optically centred in the circle.
					<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor">
						<path d="M8 5v14l11-7z" />
					</svg>
				)}
			</button>

			<div className="max-w-[120px]">
				<p className="text-xs font-medium text-gray-800 truncate">{musicName}</p>
			</div>

			<button
				type="button"
				onClick={() => setShowVolume(!showVolume)}
				aria-label={t('music.volume')}
				className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 transition flex-shrink-0"
			>
				<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
					<path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
				</svg>
			</button>

			{showVolume && (
				<input
					type="range"
					min="0"
					max="1"
					step="0.05"
					value={volume}
					onChange={(e) => setVolume(parseFloat(e.target.value))}
					className="w-16 h-1 accent-indigo-600"
				/>
			)}
		</div>
	);
};

export default memo(MusicPlayer);
